/**
 * ルーティングの純粋なパターンマッチ部分（DOM 非依存）。
 *
 * spec/design.md「画面遷移とルーティング設計」節が定める経路のみを扱う。
 * レベル内（導入ADV・演習・最終テスト）はURLを持たない単一URL内の状態遷移であり、
 * このモジュールの関知するところではない。
 *
 * D-119: `/` を門（S-0 タイトルメニュー）に譲り、稽古の段（S-1）を `/levels` へ移した。
 * 稽古の段が唯一のハブである構造（N-001）は変えていない —— 門は稽古の段の手前に立つ
 * 玄関であり、段・免状帳・稽古への入口をすべて門から辿れる。
 */
export type Route =
  | { readonly name: "title" }
  | { readonly name: "level-select" }
  | { readonly name: "level"; readonly levelId: string }
  | { readonly name: "stamps" }
  | { readonly name: "review" };

/**
 * pathname を Route へ変換する。いずれの経路にも一致しない場合は null を返す
 * （呼び出し側が N-005 に従って `/` への replaceState を行う）。
 */
export function matchRoute(pathname: string): Route | null {
  if (pathname === "/") {
    return { name: "title" };
  }

  if (pathname === "/levels") {
    return { name: "level-select" };
  }

  if (pathname === "/stamps") {
    return { name: "stamps" };
  }

  if (pathname === "/review") {
    return { name: "review" };
  }

  const levelMatch = /^\/level\/([^/]+)$/.exec(pathname);
  if (levelMatch) {
    const levelId = levelMatch[1];
    if (levelId) {
      return { name: "level", levelId };
    }
  }

  return null;
}

/** Route から対応する pathname を組み立てる（navigate 用）。matchRoute の逆変換。 */
export function routeToPath(route: Route): string {
  switch (route.name) {
    case "title":
      return "/";
    case "level-select":
      return "/levels";
    case "stamps":
      return "/stamps";
    case "review":
      return "/review";
    case "level":
      return `/level/${route.levelId}`;
    default: {
      const exhaustiveCheck: never = route;
      return exhaustiveCheck;
    }
  }
}
